import React, { useState, useEffect } from 'react';
import { Button, Card, Row, Col, Form, Table } from '@/components/ui';
import { Vehicle, VehicleBlueBookRate } from '../../../types/Vehicle';
import vehicleBlueBookRateService from '../../../services/vehicleBlueBookRateService';
import vehicleService from '../../../services/vehicleService';
import { usePermissions } from '../../../hooks/usePermissions';

interface VehicleBlueBookRatesSectionProps {
  vehicle: Vehicle;
  onUpdate: (vehicle: Vehicle) => void;
}

const VehicleBlueBookRatesSection: React.FC<VehicleBlueBookRatesSectionProps> = ({ vehicle, onUpdate }) => {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('vehicle:edit');
  const [rates, setRates] = useState<VehicleBlueBookRate[]>([]);
  const [currentRate, setCurrentRate] = useState<VehicleBlueBookRate | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRate, setEditingRate] = useState<Partial<VehicleBlueBookRate> | null>(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchRates();
    fetchCurrentRate();
  }, [vehicle.id]);

  const fetchRates = async () => {
    try {
      setLoading(true);
      const data = await vehicleBlueBookRateService.getRates(vehicle.id);
      setRates(data);
    } catch (error) {
      console.error('Error fetching rates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentRate = async () => {
    try {
      const rate = await vehicleBlueBookRateService.getCurrentRate(vehicle.id);
      setCurrentRate(rate);
    } catch (error) {
      console.error('Error fetching current rate:', error);
    }
  };

  const handleAdd = () => {
    setEditingRate({ vehicleId: vehicle.id });
    setShowForm(true);
    setIsEditing(false);
  };

  const handleEdit = (rate: VehicleBlueBookRate) => {
    setEditingRate(rate);
    setShowForm(true);
    setIsEditing(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this rate?')) {
      return;
    }

    try {
      await vehicleBlueBookRateService.deleteRate(vehicle.id, id);
      await fetchRates();
      await fetchCurrentRate();
      // Refresh vehicle data
      const updatedVehicle = await vehicleService.getVehicleById(vehicle.id);
      onUpdate(updatedVehicle);
    } catch (error) {
      console.error('Error deleting rate:', error);
      alert('Failed to delete rate');
    }
  };

  const handleSave = async () => {
    if (!editingRate) return;

    try {
      if (isEditing && editingRate.id) {
        await vehicleBlueBookRateService.updateRate(
          vehicle.id,
          editingRate.id,
          editingRate
        );
      } else {
        await vehicleBlueBookRateService.createRate(vehicle.id, editingRate);
      }
      await fetchRates();
      await fetchCurrentRate();
      setShowForm(false);
      setEditingRate(null);
      // Refresh vehicle data
      const updatedVehicle = await vehicleService.getVehicleById(vehicle.id);
      onUpdate(updatedVehicle);
    } catch (error) {
      console.error('Error saving rate:', error);
      alert('Failed to save rate');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingRate(null);
  };

  if (loading) {
    return <div>Loading rates...</div>;
  }

  // Separate current and historical rates
  // Current rate is the most recent year
  const sortedRates = [...rates].sort((a, b) => b.year - a.year);
  const currentRateData = sortedRates.length > 0 ? sortedRates[0] : null;
  const historicalRates = sortedRates.length > 1 ? sortedRates.slice(1) : [];

  return (
    <div>
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <h2>Blue Book Rates</h2>
        {canEdit && !showForm && (
          <Button variant="primary" onClick={handleAdd}>
            <i className="bi bi-plus-circle tw-mr-1"></i>Add Rate
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="tw-mb-4">
          <Card.Header>
            <h5>{isEditing ? 'Edit Rate' : 'Add Rate'}</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="tw-mb-3">
                  <Form.Label>Year</Form.Label>
                  <Form.Control
                    type="number"
                    value={editingRate?.year || ''}
                    onChange={(e) => setEditingRate({ ...editingRate, year: parseInt(e.target.value) || undefined })}
                    min="1900"
                    max="2100"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="tw-mb-3">
                  <Form.Label>Rate ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={editingRate?.rate || ''}
                    onChange={(e) => setEditingRate({ ...editingRate, rate: parseFloat(e.target.value) || undefined })}
                  />
                </div>
              </Col>
            </Row>
            <div className="tw-flex tw-justify-end">
              <Button variant="success" className="tw-mr-2" onClick={handleSave}>
                Save
              </Button>
              <Button variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Current Rate Section */}
      <Card className="tw-mb-4">
        <Card.Header className="tw-bg-primary tw-text-white">
          <h5 className="tw-mb-0">Current Rate</h5>
        </Card.Header>
        <Card.Body>
          {currentRateData ? (
            <Row>
              <Col md={6}>
                <strong>Year:</strong> {currentRateData.year}
              </Col>
              <Col md={6}>
                <strong>Rate:</strong> ${currentRateData.rate.toFixed(2)}
              </Col>
            </Row>
          ) : (
            <p className="tw-text-content-muted">No current rate set.</p>
          )}
        </Card.Body>
      </Card>

      {/* Historical Rates Section */}
      <Card>
        <Card.Header>
          <h5 className="tw-mb-0">Historical Rates</h5>
        </Card.Header>
        <Card.Body>
          {historicalRates.length === 0 ? (
            <p className="tw-text-content-muted">No historical rates found.</p>
          ) : (
            <Table striped responsive>
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Rate</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {historicalRates.map((rate) => (
                  <tr key={rate.id}>
                    <td>{rate.year}</td>
                    <td>${rate.rate.toFixed(2)}</td>
                    <td>
                      {canEdit && (
                        <>
                          <Button
                            size="sm"
                            variant="primary"
                            className="tw-mr-2"
                            onClick={() => handleEdit(rate)}
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => handleDelete(rate.id)}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default VehicleBlueBookRatesSection;
